import {
  Task,
  OrgUser,
  OrgUserWithRole,
  AssignableRole,
  CreateInspectionRequest,
  ApproveInspectionRequest,
  LandInspection,
  CreatePropertyRequest,
  CreatePropertyResponse,
  UpdatePropertyRequest,
  Property,
  PropertyActiveTask,
  SupportedRegion,
  AdminSupportedRegion,
  CreateRegionRequest,
  UpdateRegionRequest,
  CreateFeedbackRequest,
  SubmitFeedbackResponse,
  SubmitContactQueryRequest,
  FeedbackListItem,
  UpdateFeedbackStatusRequest,
  PaginatedResponse,
  SignupAttribution,
  ListAuditsResponse,
  Coordinates,
  BillingPlan,
  GatewayPrice,
  PaymentGateway,
  Checkout,
  ReferralCode,
  NotificationSettings,
  UpdateNotificationSettingsRequest,
  SendWhatsAppOtpResponse,
} from '@property/shared';
import { apiClient, edgeClient } from './api-client.js';

export interface PresignedUrlRequest {
  fileName: string;
  fileType: string;
  propertyId: string;
  inspectionId: string;
}

export interface PresignedUrlResponse {
  uploadUrl: string;
  mediaUrl: string;
  mediaId: string;
  metadata: Record<string, string>;
}

function toQueryString(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * GET /properties — paginated list
 */
async function getProperties(opts?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}): Promise<PaginatedResponse<Property>> {
  return apiClient.get<PaginatedResponse<Property>>(`/properties${toQueryString({ ...opts })}`);
}

/**
 * GET /admin/properties — all properties in the system (admin only)
 */
async function getAllProperties(opts?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}): Promise<PaginatedResponse<Property>> {
  return apiClient.get<PaginatedResponse<Property>>(
    `/admin/properties${toQueryString({ ...opts })}`,
  );
}

/**
 * Get a single property by ID
 * GET /properties/:id
 */
async function getProperty(
  id: string,
  options?: { cache?: RequestCache },
): Promise<Property | undefined> {
  try {
    return await apiClient.get<Property>(`/properties/${id}`, options);
  } catch (error) {
    // Return undefined if property not found (404)
    if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/**
 * Create a new property
 * POST /properties
 */
async function createProperty(request: CreatePropertyRequest): Promise<CreatePropertyResponse> {
  return apiClient.post<CreatePropertyResponse>('/properties', request);
}

/**
 * Update an existing property
 * PATCH /properties/:id
 */
async function updateProperty(
  propertyId: string,
  updates: UpdatePropertyRequest,
): Promise<Property> {
  return apiClient.patch<Property>(`/properties/${propertyId}`, updates);
}

/**
 * GET /properties/:propertyId/inspections
 */
async function getPropertyReports(propertyId: string): Promise<LandInspection[]> {
  return apiClient.get<LandInspection[]>(`/properties/${propertyId}/inspections`);
}

/**
 * GET /properties/:propertyId/active-task
 */
async function getPropertyActiveTask(propertyId: string): Promise<PropertyActiveTask | null> {
  return apiClient.get<PropertyActiveTask | null>(`/properties/${propertyId}/active-task`);
}

/**
 * GET /inspections — all inspections (admin/reviewer)
 */
async function getAllInspections(): Promise<LandInspection[]> {
  return apiClient.get<LandInspection[]>('/inspections');
}

/**
 * Get the inspection linked to a task
 * GET /tasks/:taskId/inspection
 */
async function getTaskInspection(taskId: string): Promise<LandInspection | undefined> {
  try {
    return await apiClient.get<LandInspection>(`/tasks/${taskId}/inspection`);
  } catch (error) {
    if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/**
 * Get a single inspection report by ID
 * GET /inspections/:id
 */
async function getReport(id: string): Promise<LandInspection | undefined> {
  try {
    return await apiClient.get<LandInspection>(`/inspections/${id}`);
  } catch (error) {
    if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/**
 * Generate a report PDF and get a short-lived presigned download URL.
 * GET /inspections/:id/pdf
 */
async function getReportPdfUrl(id: string): Promise<{ url: string }> {
  return apiClient.get<{ url: string }>(`/inspections/${id}/pdf`);
}

/**
 * GET /tasks — paginated list with optional status and type filters
 */
async function getTasks(opts?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
  type?: string;
  search?: string;
}): Promise<PaginatedResponse<Task>> {
  return apiClient.get<PaginatedResponse<Task>>(`/tasks${toQueryString({ ...opts })}`);
}

/**
 * Get a single task by ID
 * GET /tasks/:id
 */
async function getTask(id: string): Promise<Task | undefined> {
  try {
    return await apiClient.get<Task>(`/tasks/${id}`);
  } catch (error) {
    if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/**
 * Submit an inspection report
 * POST /inspections
 */
async function submitInspectionReport(
  request: CreateInspectionRequest,
): Promise<{ success: boolean; inspectionId: string }> {
  return apiClient.post<{ success: boolean; inspectionId: string }>('/inspections', request);
}

/**
 * Toggle auto-renewal for a property's subscription
 * PATCH /properties/:propertyId/auto-renewal
 */
async function toggleAutoRenewal(propertyId: string, enabled: boolean): Promise<Property> {
  return apiClient.patch<Property>(`/properties/${propertyId}/auto-renewal`, { enabled });
}

/**
 * Stop monitoring a property
 * PATCH /properties/:propertyId/stop-monitoring
 */
async function stopMonitoring(propertyId: string): Promise<Property> {
  return apiClient.patch<Property>(`/properties/${propertyId}/stop-monitoring`, {});
}

/**
 * Archive a property (must be in STOPPED state) — deletes all media and hides from dashboard
 * PATCH /properties/:propertyId/archive
 */
async function archiveProperty(propertyId: string): Promise<void> {
  await apiClient.patch(`/properties/${propertyId}/archive`, {});
}

/**
 * Get all supported regions
 * GET /regions
 * Note: This is a public endpoint, no auth required
 */
async function getRegions(): Promise<SupportedRegion[]> {
  return apiClient.get<SupportedRegion[]>('/regions', { requiresAuth: false });
}

/**
 * GET /inspections/queue — paginated review queue (pending or reviewed)
 */
async function getReviewQueue(opts: {
  status: 'pending_review' | 'reviewed';
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}): Promise<PaginatedResponse<LandInspection>> {
  return apiClient.get<PaginatedResponse<LandInspection>>(
    `/inspections/queue${toQueryString({ ...opts })}`,
  );
}

/**
 * POST /inspections/:id/approve — approve an inspection with optional notes
 */
async function approveInspection(
  id: string,
  request: ApproveInspectionRequest,
): Promise<LandInspection> {
  return apiClient.post<LandInspection>(`/inspections/${id}/approve`, request);
}

async function rejectInspection(
  id: string,
  request: ApproveInspectionRequest,
): Promise<LandInspection> {
  return apiClient.post<LandInspection>(`/inspections/${id}/reject`, request);
}

/**
 * Submit feedback (general or inspection-report-specific)
 * POST /feedback
 */
async function submitFeedback(request: CreateFeedbackRequest): Promise<SubmitFeedbackResponse> {
  return apiClient.post<SubmitFeedbackResponse>('/feedback', request);
}

/**
 * Submit a contact query (public — no auth required, but user_id captured if signed in)
 * POST /contact
 */
async function submitContactQuery(
  request: SubmitContactQueryRequest,
): Promise<SubmitFeedbackResponse> {
  return apiClient.post<SubmitFeedbackResponse>('/contact', request, { requiresAuth: false });
}

/**
 * GET /feedback — paginated list with optional type filter
 */
async function getFeedback(opts?: {
  type?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<PaginatedResponse<FeedbackListItem>> {
  return apiClient.get<PaginatedResponse<FeedbackListItem>>(
    `/feedback${toQueryString({ ...opts })}`,
  );
}

/**
 * Mark a feedback item as read or unread (admin)
 * PATCH /feedback/:id/status
 */
async function updateFeedbackStatus(
  id: number,
  status: UpdateFeedbackStatusRequest['status'],
): Promise<void> {
  await apiClient.patch(`/feedback/${id}/status`, { status });
}

/**
 * GET /users/:id
 */
async function getUserProfile(
  userId: string,
): Promise<{ name: string | null; profilePictureUrl: string | null }> {
  return apiClient.get<{ name: string | null; profilePictureUrl: string | null }>(
    `/users/${userId}`,
  );
}

/**
 * Search org users by role and free-text (name or email).
 * Requires search.length >= 3 — enforced by the server and the calling hook.
 * GET /users?role=agent&search=...
 */
async function searchUsers(role: string, search: string): Promise<OrgUser[]> {
  const result = await apiClient.get<{ users: OrgUser[] }>(
    `/users?role=${encodeURIComponent(role)}&search=${encodeURIComponent(search)}`,
  );
  return result.users;
}

/**
 * Bulk-assign a list of tasks to a single agent.
 * POST /tasks/bulk-assign
 */
async function bulkAssignTasks(taskIds: string[], agentId: string): Promise<{ assigned: number }> {
  return apiClient.post<{ assigned: number }>('/tasks/bulk-assign', { taskIds, agentId });
}

/**
 * Search all org users across all roles (admin use only).
 * GET /admin/users?search=...
 */
async function searchAllUsers(search: string): Promise<OrgUserWithRole[]> {
  const result = await apiClient.get<{ users: OrgUserWithRole[] }>(
    `/admin/users?search=${encodeURIComponent(search)}`,
  );
  return result.users;
}

/**
 * List staff (admin, reviewer, agent) users with pagination (admin use only).
 * GET /admin/staff?page=...&limit=...
 */
async function listStaff(opts?: {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<PaginatedResponse<OrgUserWithRole>> {
  return apiClient.get<PaginatedResponse<OrgUserWithRole>>(
    `/admin/staff${toQueryString({ ...opts })}`,
  );
}

/**
 * Get the number of active properties owned by a user (admin only).
 * GET /admin/users/:id/property-count
 */
async function getUserPropertyCount(userId: string): Promise<number> {
  const result = await apiClient.get<{ count: number }>(`/admin/users/${userId}/property-count`);
  return result.count;
}

/**
 * Change a user's role. Admins cannot be downgraded — returns 403 if target is admin.
 * PATCH /users/:id/role
 */
async function switchUserRole(userId: string, role: AssignableRole): Promise<void> {
  await apiClient.patch(`/users/${userId}/role`, { role });
}

/**
 * Get a presigned S3 upload URL
 * POST /media/presigned-url
 */
async function getPresignedUrl(request: PresignedUrlRequest): Promise<PresignedUrlResponse> {
  return apiClient.post<PresignedUrlResponse>('/media/presigned-url', request);
}

/**
 * Record signup attribution (first-write-wins per user)
 * POST /attribution
 */
async function recordAttribution(data: SignupAttribution): Promise<void> {
  await apiClient.post('/attribution', data);
}

/**
 * Get the authenticated user's personal referral link (generated on first request)
 * GET /users/me/referral-code
 */
async function getReferralCode(): Promise<ReferralCode> {
  return apiClient.get<ReferralCode>('/users/me/referral-code');
}

/**
 * Record an out-of-service-area map click for demand analysis
 * POST /demand-signals
 */
async function recordDemandSignal(coords: Coordinates): Promise<void> {
  await apiClient.post('/demand-signals', coords);
}

/**
 * Assign a single task to an agent
 * PATCH /tasks/:taskId/assign
 */
async function assignTask(taskId: string, agentId: string): Promise<Task> {
  return apiClient.patch<Task>(`/tasks/${taskId}/assign`, { agentId });
}

async function declineTask(taskId: string): Promise<Task> {
  return apiClient.post<Task>(`/tasks/${taskId}/decline`, {});
}

/**
 * GET /admin/audits — paginated audit log (admin only)
 */
async function listAudits(opts?: { page?: number; limit?: number }): Promise<ListAuditsResponse> {
  return apiClient.get<ListAuditsResponse>(`/admin/audits${toQueryString({ ...opts })}`);
}

/**
 * GET /admin/regions — all regions including inactive (admin only)
 */
async function getAdminRegions(): Promise<AdminSupportedRegion[]> {
  return apiClient.get<AdminSupportedRegion[]>('/admin/regions');
}

/**
 * POST /admin/regions — create a new region (admin only)
 */
async function createRegion(request: CreateRegionRequest): Promise<AdminSupportedRegion> {
  return apiClient.post<AdminSupportedRegion>('/admin/regions', request);
}

/**
 * PUT /admin/regions/:id — update an existing region (admin only)
 */
async function updateRegion(
  id: number,
  request: UpdateRegionRequest,
): Promise<AdminSupportedRegion> {
  return apiClient.put<AdminSupportedRegion>(`/admin/regions/${id}`, request);
}

/**
 * GET /__geo — served at the edge by CloudFront Function (not the API).
 * Returns the GeoIP-detected country code for billing-country prefill.
 */
async function getGeoCountry(): Promise<{ countryCode: string | null }> {
  return edgeClient.get<{ countryCode: string | null }>('/__geo', {
    requiresAuth: false,
    headers: { Accept: 'application/json' },
  });
}

/**
 * GET /billing/plans?country= — the sole pricing source; the frontend never
 * holds a price constant.
 */
async function getBillingPlans(country: string, regionId?: string | null): Promise<BillingPlan[]> {
  return apiClient.get<BillingPlan[]>(
    `/billing/plans${toQueryString({ country, regionId: regionId || undefined })}`,
  );
}

/**
 * GET /billing/gateway-price — general-purpose live price lookup by gateway + gateway
 * plan/price id. 404s (thrown as ApiError) when the id doesn't resolve on the gateway —
 * callers decide how to surface that (e.g. an inline "couldn't find this price" message).
 */
async function getGatewayPrice(
  gateway: PaymentGateway,
  gatewayPlanId: string,
): Promise<GatewayPrice> {
  return apiClient.get<GatewayPrice>(
    `/billing/gateway-price${toQueryString({ gateway, gatewayPlanId })}`,
  );
}

/**
 * POST /billing/checkout — idempotent: returns the existing unexpired checkout
 * URL if one exists, else mints a new one.
 */
async function createCheckout(propertyId: string): Promise<Checkout> {
  return apiClient.post<Checkout>('/billing/checkout', { propertyId });
}

/**
 * GET /notifications — the authenticated user's phone number + channel preferences
 */
async function getNotificationSettings(): Promise<NotificationSettings> {
  return apiClient.get<NotificationSettings>('/notifications');
}

/**
 * PUT /notifications — channel preferences only; the phone number is set via the
 * WhatsApp OTP verify flow below.
 */
async function updateNotificationSettings(
  request: UpdateNotificationSettingsRequest,
): Promise<NotificationSettings> {
  return apiClient.put<NotificationSettings>('/notifications', request);
}

/**
 * POST /notifications/whatsapp/otp/send — starts a WhatsApp verification via Twilio Verify,
 * which owns the code, its expiry and delivery. The response never carries the code.
 */
async function sendWhatsAppOtp(phoneNumber: string): Promise<SendWhatsAppOtpResponse> {
  return apiClient.post<SendWhatsAppOtpResponse>('/notifications/whatsapp/otp/send', {
    phoneNumber,
  });
}

/**
 * POST /notifications/whatsapp/otp/verify
 */
async function verifyWhatsAppOtp(phoneNumber: string, code: string): Promise<NotificationSettings> {
  return apiClient.post<NotificationSettings>('/notifications/whatsapp/otp/verify', {
    phoneNumber,
    code,
  });
}

/**
 * DELETE /notifications/whatsapp — removes the verified number; the server also
 * disables the whatsapp preference in the same call.
 */
async function removeWhatsAppNumber(): Promise<NotificationSettings> {
  return apiClient.delete<NotificationSettings>('/notifications/whatsapp');
}

/**
 * Exported API
 */
export const api = {
  getProperties,
  getAllProperties,
  getProperty,
  createProperty,
  updateProperty,
  toggleAutoRenewal,
  stopMonitoring,
  archiveProperty,
  getPropertyReports,
  getPropertyActiveTask,
  getAllInspections,
  getReport,
  getReportPdfUrl,
  getTaskInspection,
  getTasks,
  getTask,
  submitInspectionReport,
  getRegions,
  getReviewQueue,
  approveInspection,
  rejectInspection,
  submitFeedback,
  submitContactQuery,
  getFeedback,
  updateFeedbackStatus,
  getUserProfile,
  searchUsers,
  bulkAssignTasks,
  assignTask,
  declineTask,
  searchAllUsers,
  listStaff,
  getUserPropertyCount,
  switchUserRole,
  getPresignedUrl,
  recordAttribution,
  getReferralCode,
  recordDemandSignal,
  listAudits,
  getAdminRegions,
  createRegion,
  updateRegion,
  getGeoCountry,
  getBillingPlans,
  getGatewayPrice,
  createCheckout,
  getNotificationSettings,
  updateNotificationSettings,
  sendWhatsAppOtp,
  verifyWhatsAppOtp,
  removeWhatsAppNumber,
};
